import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTRPC, useTRPCClient } from '~/utils/trpc'
import { plural } from '~/utils/plural'
import type { SiteDraft, StationDraft } from '../batchDraft'
import { SITE_STATIONS_STALE_MS } from '../constants'
import {
  parseCsv,
  parseSpreadsheet,
  toSiteDrafts,
  toStationDrafts,
  type CsvIssue,
  type ParseOutcome,
  type SiteStation
} from '../csv'
import { CsvStepImport } from './CsvStepImport'

/** What one step was filled with. */
export interface AppliedCsv {
  fileName: string
  count: number
  issues: CsvIssue[]
}

interface PendingFile {
  fileName: string
  outcome: ParseOutcome
}

const readFile = async (file: File): Promise<PendingFile> => ({
  fileName: file.name,
  outcome: file.name.toLowerCase().endsWith('.xlsx') ? await parseSpreadsheet(file) : parseCsv(await file.text())
})

const useSiteStations = () => {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  return async (sites: SiteDraft[]): Promise<Record<string, SiteStation[]>> => {
    const siteIds = sites.flatMap((site) => (site.siteId ? [site.siteId] : []))
    const lists = await Promise.all(
      siteIds.map((siteId) =>
        queryClient.fetchQuery({
          ...trpc.ops.stationInstallations.listSiteStations.queryOptions({ siteId }),
          staleTime: SITE_STATIONS_STALE_MS
        })
      )
    )
    return Object.fromEntries(siteIds.map((siteId, index) => [siteId, lists[index]]))
  }
}

/** The file waiting to be applied, and the props every step's import card shares. */
const useCsvImport = <T,>({
  applied,
  apply,
  onApply,
  onDismiss
}: {
  applied: AppliedCsv | null
  apply: (file: PendingFile) => Promise<T>
  onApply: (result: T) => void
  onDismiss: () => void
}) => {
  const [pending, setPending] = useState<PendingFile | null>(null)
  const applyMutation = useMutation({
    mutationFn: apply,
    onSuccess: (result) => {
      setPending(null)
      onApply(result)
    }
  })

  const cardProps = {
    fileName: pending?.fileName ?? applied?.fileName ?? null,
    isApplied: !pending && !!applied,
    fatal: pending?.outcome.fatal ?? [],
    error: applyMutation.error?.message ?? null,
    applying: applyMutation.isPending,
    onFile: async (file: File) => {
      applyMutation.reset()
      setPending(await readFile(file))
    },
    onApply: () => {
      if (pending) applyMutation.mutate(pending)
    },
    onClear: () => {
      applyMutation.reset()
      if (pending) setPending(null)
      else onDismiss()
    }
  }
  return { pending, cardProps }
}

type SitePlan = ReturnType<typeof toSiteDrafts>

interface SitesApplied {
  sites: SiteDraft[]
  stations: StationDraft[]
  sitesCsv: AppliedCsv
  stationsCsv: AppliedCsv
}

interface SitesCsvImportProps {
  orgId: string
  applied: AppliedCsv | null
  disabled: boolean
  onApply: (result: SitesApplied) => void
  onDismiss: () => void
}

/** Fills the Sites step, and the Stations step from the same rows, so one file covers the batch. */
export const SitesCsvImport = ({ orgId, applied, disabled, onApply, onDismiss }: SitesCsvImportProps) => {
  const trpc = useTRPC()
  const trpcClient = useTRPCClient()
  const queryClient = useQueryClient()
  const fetchSiteStations = useSiteStations()

  const sitesQuery = useQuery(trpc.ops.sites.list.queryOptions({ orgId }))
  const contactsQuery = useQuery(trpc.ops.contacts.list.queryOptions({ orgId }))

  const writeContacts = async (plan: SitePlan) => {
    const writes = await Promise.allSettled([
      ...plan.contactsToCreate.map((fields) => trpcClient.ops.contacts.create.mutate({ ...fields, orgId })),
      ...plan.contactsToUpdate.map((input) => trpcClient.ops.contacts.update.mutate(input))
    ])
    // Read back even when a write failed, so a retry plans against the contacts that did save rather
    // than creating them a second time. Other orgs' lists are only marked stale.
    await queryClient.invalidateQueries({ queryKey: trpc.ops.contacts.list.queryKey(), refetchType: 'none' })
    const contacts = await queryClient.fetchQuery(trpc.ops.contacts.list.queryOptions({ orgId }))
    const failure = writes.find((write): write is PromiseRejectedResult => write.status === 'rejected')
    if (failure) throw failure.reason
    return contacts
  }

  const { pending, cardProps } = useCsvImport({
    applied,
    onApply,
    onDismiss,
    apply: async ({ fileName, outcome }): Promise<SitesApplied> => {
      if (!plan || !sitesQuery.data) throw new Error('The organization is still loading — try again in a moment')
      const changesContacts = plan.contactsToCreate.length > 0 || plan.contactsToUpdate.length > 0
      const { sites, issues } = changesContacts
        ? toSiteDrafts(outcome.rows, sitesQuery.data, await writeContacts(plan))
        : plan
      const stationPlan = toStationDrafts(outcome.rows, sites, await fetchSiteStations(sites))
      return {
        sites,
        stations: stationPlan.stations,
        sitesCsv: { fileName, count: sites.length, issues },
        stationsCsv: { fileName, count: stationPlan.stations.length, issues: stationPlan.issues }
      }
    }
  })

  const plan = useMemo<SitePlan | null>(
    () =>
      pending && sitesQuery.data && contactsQuery.data
        ? toSiteDrafts(pending.outcome.rows, sitesQuery.data, contactsQuery.data)
        : null,
    [pending, sitesQuery.data, contactsQuery.data]
  )

  const planSummary = plan
    ? [
        `${plural(plan.sites.length, 'site')} (${plan.sites.filter((site) => site.siteId === null).length} new)`,
        `${plural(plan.contactsToCreate.length, 'contact')} to create`,
        ...(plan.contactsToUpdate.length > 0
          ? [`${plural(plan.contactsToUpdate.length, 'contact')} to give an address`]
          : []),
        plural(pending?.outcome.rows.length ?? 0, 'station row')
      ].join(' · ')
    : null
  const appliedSummary = applied ? `Filled ${plural(applied.count, 'site')} and the Stations step from this file` : null

  return (
    <CsvStepImport
      {...cardProps}
      description="One row per meter. Fills the sites, their contacts and the Stations step. Replaces what is here."
      summary={pending ? planSummary : appliedSummary}
      issues={pending ? (plan?.issues ?? []) : (applied?.issues ?? [])}
      applyLabel="Apply to sites and stations"
      disabled={disabled}
    />
  )
}

interface StationsCsvImportProps {
  sites: SiteDraft[]
  applied: AppliedCsv | null
  disabled: boolean
  onApply: (result: { stations: StationDraft[]; stationsCsv: AppliedCsv }) => void
  onDismiss: () => void
}

export const StationsCsvImport = ({ sites, applied, disabled, onApply, onDismiss }: StationsCsvImportProps) => {
  const fetchSiteStations = useSiteStations()

  const { pending, cardProps } = useCsvImport({
    applied,
    onApply,
    onDismiss,
    apply: async ({ fileName, outcome }) => {
      const { stations, issues } = toStationDrafts(outcome.rows, sites, await fetchSiteStations(sites))
      return { stations, stationsCsv: { fileName, count: stations.length, issues } }
    }
  })

  const pendingSummary =
    pending && pending.outcome.fatal.length === 0
      ? `${plural(pending.outcome.rows.length, 'station row')}, matched to the sites in this batch by site_name`
      : null
  const appliedSummary = applied ? `Filled ${plural(applied.count, 'station')} from this file` : null

  return (
    <CsvStepImport
      {...cardProps}
      description="One row per meter, matched to this batch's sites by site_name. Replaces every station below."
      summary={pending ? pendingSummary : appliedSummary}
      issues={pending ? [] : (applied?.issues ?? [])}
      applyLabel="Apply to stations"
      disabled={disabled}
    />
  )
}
